// LeaderPortalNotice.tsx
/**
 * Inline failure state for the leader portal pages (My Cases, Pending Cases).
 *
 * These endpoints 403 with "You are not registered as a care leader" when the signed-in account holds
 * the ActsCareLeader *role* but has no Leader *record* behind it. That is a setup problem the user can
 * act on, so it belongs in the page body. A toast disappears and leaves an empty table that reads like
 * a bug.
 */
import { Card, Stack, Text, ThemeIcon, Button } from '@mantine/core';
import { IconAlertTriangle, IconRefresh, IconUserExclamation } from '@tabler/icons-react';

/** Pull status/message off a thrown SDK `CCMSError` (or anything else) without assuming its shape. */
function readApiError(error: unknown): { status?: number; message: string } {
  const e = error as { status?: number; statusCode?: number; message?: string } | null;
  return {
    status: e?.status ?? e?.statusCode,
    message: e?.message ?? '',
  };
}

/** True when the backend says this account has no care-leader record. */
function isNotRegisteredAsLeader(error: unknown): boolean {
  const { status, message } = readApiError(error);
  if (status === 403) return true;
  return /not registered as a care leader/i.test(message);
}

interface LeaderPortalNoticeProps {
  error: unknown;
  /** What the page was trying to load, e.g. "your cases" — used in the generic message. */
  subject: string;
  onRetry: () => void;
}

export default function LeaderPortalNotice({ error, subject, onRetry }: LeaderPortalNoticeProps) {
  const notALeader = isNotRegisteredAsLeader(error);

  return (
    <Card withBorder radius="md" padding="xl">
      <Stack align="center" gap="sm">
        <ThemeIcon size={56} radius="md" variant="light" color={notALeader ? 'yellow' : 'red'}>
          {notALeader ? <IconUserExclamation size={28} /> : <IconAlertTriangle size={28} />}
        </ThemeIcon>

        <Text fw={600} size="lg" ta="center">
          {notALeader ? 'You are not registered as a care leader' : `Could not load ${subject}`}
        </Text>

        <Text size="sm" c="dimmed" ta="center" maw={520}>
          {notALeader ? (
            <>
              Your account has the care leader role, but no care support leader profile is linked to it
              yet, so there are no cases to show. Ask the care office to add you under
              <Text span fw={600}> Information &gt; Care Support Leaders</Text>, then try again.
            </>
          ) : (
            <>
              Something went wrong whilst loading {subject}. This is usually temporary. Try again, and
              let the care office know if it keeps happening.
            </>
          )}
        </Text>

        <Button variant="light" leftSection={<IconRefresh size={16} />} onClick={onRetry} mt="xs">
          Try again
        </Button>
      </Stack>
    </Card>
  );
}
